// Mock data for Apex Build Project Management consulting website

export const companyInfo = {
  name: "Apex Build Project Management",
  shortName: "Apex Build",
  tagline: "Lead smarter. Build better.",
  about: "Apex Build Project Management provides independent project leadership and advisory services across infrastructure, telecommunications, and commercial developments. Led by a senior Project Director with extensive experience delivering major infrastructure, telecommunications, utilities, and commercial developments across Australia. We support clients in delivering complex projects with clarity, control, and strong governance.",
  stats: {
    projectValue: 2500, // in millions
    projectsDelivered: 50,
    sectors: 6,
    yearsExperience: 25
  },
  leadership: {
    title: "Senior Project Director",
    experience: "25+ years of senior-level project leadership across Australia's major infrastructure, telecommunications, utilities, and commercial sectors. Proven track record delivering complex, high-value projects with strategic clarity and operational excellence.",
    expertise: [
      "Strategic project direction for infrastructure, telecommunications, and utilities programs",
      "Senior leadership on projects exceeding $2.5 billion in cumulative value",
      "Governance and delivery frameworks for complex, multi-stakeholder environments",
      "Client-side representation and independent advisory across major sectors",
      "Risk management and commercial control for large-scale programs"
    ]
  }
};

export const capabilities = [
  {
    id: 1,
    category: "Governance & Oversight",
    description: "Establishing robust governance frameworks and providing strategic oversight throughout project lifecycle.",
    details: [
      "Project governance structure design and implementation",
      "Board and executive reporting frameworks",
      "Independent project health reviews and assessments",
      "Compliance and assurance frameworks"
    ]
  },
  {
    id: 2,
    category: "Strategic Planning",
    description: "Developing comprehensive project strategies aligned with organizational objectives and delivery constraints.",
    details: [
      "Strategic project structuring and delivery planning",
      "Procurement and contracting strategy development",
      "Stakeholder analysis and engagement planning",
      "Project delivery methodology selection and tailoring"
    ]
  },
  {
    id: 3,
    category: "Delivery Leadership",
    description: "Providing senior-level project direction ensuring delivery aligns with strategic objectives and quality standards.",
    details: [
      "End-to-end project direction and delivery oversight",
      "Contractor and consultant performance management",
      "Quality assurance and delivery coordination",
      "Issue resolution and decision-making support"
    ]
  },
  {
    id: 4,
    category: "Risk & Commercial Control",
    description: "Implementing comprehensive risk management and commercial control frameworks for project success.",
    details: [
      "Risk identification, assessment, and mitigation strategies",
      "Cost management and budget control frameworks",
      "Schedule management and critical path optimization",
      "Change management and commercial impact assessment"
    ]
  },
  {
    id: 5,
    category: "Stakeholder & Contractor Management",
    description: "Coordinating complex stakeholder environments and managing contractor relationships for collaborative delivery.",
    details: [
      "Stakeholder engagement and communications strategies",
      "Contractor relationship and performance management",
      "Interface coordination across multiple parties",
      "Dispute avoidance and resolution support"
    ]
  }
];

export const whyApexReasons = [
  {
    id: 1,
    title: "Independent Advisory",
    description: "Objective, client-focused project direction protecting your interests throughout delivery.",
    icon: "shield"
  },
  {
    id: 2,
    title: "Senior-Level Leadership",
    description: "Experienced Project Director leadership with 25+ years across major infrastructure sectors.",
    icon: "award"
  },
  {
    id: 3,
    title: "Clear Governance",
    description: "Structured governance frameworks providing clarity, control, and confidence in delivery.",
    icon: "check-circle"
  },
  {
    id: 4,
    title: "Client-Side Representation",
    description: "Acting as your representative, ensuring project outcomes align with strategic objectives.",
    icon: "users"
  },
  {
    id: 5,
    title: "Complex Infrastructure Experience",
    description: "Proven delivery across telecommunications, utilities, transport, and commercial sectors.",
    icon: "building"
  }
];

export const deliveryFramework = [
  {
    id: 1,
    stage: "Discover",
    title: "Requirements & Constraints",
    description: "Understanding project objectives, constraints, and stakeholder expectations.",
    details: "Comprehensive project scoping including stakeholder analysis, constraints mapping, success criteria definition, and delivery environment assessment. Establishing clear understanding of project requirements, organizational context, and delivery challenges.",
    outcomes: [
      "Clear project scope and objectives",
      "Stakeholder expectations mapped",
      "Constraints and risks identified",
      "Success criteria established"
    ]
  },
  {
    id: 2,
    stage: "Structure",
    title: "Strategy & Governance",
    description: "Developing project strategy, governance frameworks, and delivery planning.",
    details: "Strategic project structuring including governance framework design, delivery methodology selection, procurement strategy development, and organizational structure definition. Creating robust foundations for successful project delivery.",
    outcomes: [
      "Governance framework established",
      "Delivery strategy defined",
      "Procurement approach determined",
      "Project organization structured"
    ]
  },
  {
    id: 3,
    stage: "Govern",
    title: "Framework Implementation",
    description: "Implementing governance structures and establishing delivery controls.",
    details: "Operationalizing governance frameworks including reporting structures, decision-making processes, risk management systems, and performance measurement frameworks. Establishing control mechanisms ensuring delivery visibility and responsiveness.",
    outcomes: [
      "Governance processes operational",
      "Reporting frameworks active",
      "Control systems implemented",
      "Performance measures established"
    ]
  },
  {
    id: 4,
    stage: "Deliver",
    title: "Oversight & Coordination",
    description: "Active project oversight, contractor coordination, and delivery management.",
    details: "Providing strategic project direction including contractor performance management, stakeholder coordination, issue resolution, and delivery assurance. Maintaining delivery momentum while managing risks and ensuring quality outcomes.",
    outcomes: [
      "Delivery progress maintained",
      "Contractors performing",
      "Issues resolved proactively",
      "Quality standards met"
    ]
  },
  {
    id: 5,
    stage: "Assure",
    title: "Risk, Reporting, Quality",
    description: "Continuous risk management, performance reporting, and quality assurance.",
    details: "Ongoing delivery assurance including risk monitoring and mitigation, performance reporting to stakeholders, quality control verification, and schedule/cost management. Maintaining delivery confidence through systematic monitoring and control.",
    outcomes: [
      "Risks managed effectively",
      "Performance reported transparently",
      "Quality maintained consistently",
      "Schedule and budget controlled"
    ]
  }
];

export const services = [
  {
    id: 1,
    title: "Project Direction & Delivery Oversight",
    description: "End-to-end project leadership ensuring delivery aligns with strategic objectives and quality standards.",
    details: "Comprehensive project direction providing executive-level oversight, strategic guidance, and delivery assurance for complex infrastructure and commercial projects."
  },
  {
    id: 2,
    title: "Strategic Planning & Project Structuring",
    description: "Comprehensive project structuring and strategic planning to establish clear frameworks for success.",
    details: "Development of robust project frameworks, governance structures, and strategic delivery plans aligned with organizational objectives."
  },
  {
    id: 3,
    title: "Risk, Cost & Schedule Management",
    description: "Proactive management of project risks, budgets, and timelines to maintain control and predictability.",
    details: "Implementation of comprehensive risk management frameworks, cost control systems, and schedule optimization strategies."
  },
  {
    id: 4,
    title: "Independent Project Reviews",
    description: "Objective assessment of project health, governance, and delivery capability with actionable recommendations.",
    details: "Expert independent reviews providing unbiased assessment of project status, risks, and opportunities for improvement."
  },
  {
    id: 5,
    title: "Stakeholder & Contractor Management",
    description: "Effective coordination across all project parties to ensure alignment and collaborative delivery.",
    details: "Strategic stakeholder engagement and contractor coordination ensuring alignment, performance, and collaborative project delivery."
  },
  {
    id: 6,
    title: "Client-Side / Owner's Representative Services",
    description: "Representing client interests throughout project lifecycle with experienced oversight and governance.",
    details: "Acting as the client's representative, protecting interests and ensuring project outcomes align with strategic objectives."
  }
];

export const sectors = [
  {
    id: 1,
    name: "Infrastructure",
    description: "Major transport, rail, and civil infrastructure projects requiring strategic oversight and delivery excellence.",
    icon: "building"
  },
  {
    id: 2,
    name: "Telecommunications",
    description: "Large-scale telecommunications network design, construction, and delivery across fixed and mobile infrastructure.",
    icon: "network"
  },
  {
    id: 3,
    name: "Utilities / Power",
    description: "Transmission, distribution, and substation projects for electricity infrastructure and utilities.",
    icon: "zap"
  },
  {
    id: 4,
    name: "Roads & Civil",
    description: "Highway upgrades, road widening, and civil infrastructure across urban and regional environments.",
    icon: "map"
  },
  {
    id: 5,
    name: "Commercial & Industrial",
    description: "Commercial developments, industrial facilities, and mixed-use projects requiring expert project direction.",
    icon: "briefcase"
  }
];

export const portfolio = [
  {
    id: 1,
    title: "National Broadband Network",
    subtitle: "FTTN, FTTC, FTTB, N2P, Fibrelinks",
    sector: "Telecommunications",
    category: "telecom",
    value: "$600M+",
    location: "Multiple regions, Australia",
    description: "Major telecommunications infrastructure delivery across multiple network technologies and regions.",
    role: "Project Director - Strategic oversight and delivery leadership",
    scope: [
      "Multi-technology network deployment across FTTN, FTTC, FTTB platforms",
      "Coordination of multiple contractor packages and technology integration",
      "Stakeholder management across government, contractors, and community",
      "Quality assurance and delivery governance frameworks"
    ],
    outcomes: "Delivered on schedule with robust governance. Established quality frameworks ensuring long-term network performance and stakeholder satisfaction."
  },
  {
    id: 2,
    title: "Telstra Network Design & Construction",
    subtitle: "National telecommunications infrastructure",
    sector: "Telecommunications",
    category: "telecom",
    value: "$500M",
    location: "Australia-wide",
    description: "Large-scale network design and construction management for national telecommunications provider.",
    role: "Senior Project Leadership - Network delivery and contractor oversight",
    scope: [
      "Network design validation and construction oversight",
      "Multi-contractor coordination and performance management",
      "Risk and cost control across complex delivery environment",
      "Quality and compliance assurance"
    ],
    outcomes: "Successfully delivered network expansion meeting strategic objectives. Maintained cost control and schedule performance across complex multi-site program."
  },
  {
    id: 3,
    title: "Pacific Highway Upgrades",
    subtitle: "Multiple construction packages",
    sector: "Transport Infrastructure",
    category: "roads",
    value: "$300M+",
    location: "NSW, Australia",
    description: "Strategic oversight of major highway upgrade program across multiple construction packages.",
    role: "Project Director - Program oversight and delivery coordination",
    scope: [
      "Multi-package program coordination and integration",
      "Stakeholder engagement across government, contractors, and community",
      "Risk management and delivery assurance",
      "Quality control and environmental compliance"
    ],
    outcomes: "Delivered complex highway upgrades maintaining schedule and budget performance. Strong stakeholder coordination and community engagement throughout delivery."
  },
  {
    id: 4,
    title: "Transmission Cable Alliance",
    subtitle: "TraCa Alliance - Multiple projects",
    sector: "Utilities Infrastructure",
    category: "utilities",
    value: "$250M",
    location: "Queensland, Australia",
    description: "Alliance project management for major transmission cable infrastructure program.",
    role: "Alliance Project Director - Strategic leadership and delivery oversight",
    scope: [
      "Alliance governance and collaborative delivery frameworks",
      "Multi-project program coordination and risk management",
      "Contractor and stakeholder alignment",
      "Commercial and schedule performance management"
    ],
    outcomes: "Successful alliance delivery model implementation. Achieved cost and schedule targets with strong collaborative relationships."
  },
  {
    id: 5,
    title: "Southern Sydney Freight Line",
    subtitle: "Rail infrastructure delivery",
    sector: "Rail Infrastructure",
    category: "infrastructure",
    value: "$150M",
    location: "Sydney, NSW",
    description: "Project direction for critical rail freight infrastructure delivery.",
    role: "Project Director - Client-side leadership and oversight",
    scope: [
      "Strategic project direction and client representation",
      "Contractor performance management and delivery assurance",
      "Risk and cost control implementation",
      "Stakeholder coordination and reporting"
    ],
    outcomes: "Delivered critical rail infrastructure on time and budget. Maintained strong governance and stakeholder alignment throughout delivery."
  },
  {
    id: 6,
    title: "Powerlink Substations",
    subtitle: "275kV and 132kV substations",
    sector: "Energy Infrastructure",
    category: "utilities",
    value: "$150M",
    location: "Queensland, Australia",
    description: "Substation construction and upgrade program across multiple sites.",
    role: "Senior Project Oversight - Multi-site program coordination",
    scope: [
      "Multi-site substation program management",
      "Technical delivery oversight and quality assurance",
      "Safety and environmental compliance",
      "Schedule and cost performance management"
    ],
    outcomes: "Successfully delivered substation program meeting technical and performance requirements. Strong safety record and environmental compliance."
  },
  {
    id: 7,
    title: "NSW Transport Authority",
    subtitle: "Transport infrastructure program",
    sector: "Transport",
    category: "infrastructure",
    value: "$68M",
    location: "NSW, Australia",
    description: "Strategic project management and delivery oversight for transport authority projects.",
    role: "Project Director - Strategic oversight and governance",
    scope: [
      "Project governance and delivery frameworks",
      "Contractor coordination and performance management",
      "Risk management and reporting",
      "Stakeholder engagement and alignment"
    ],
    outcomes: "Delivered transport projects with robust governance. Maintained strong stakeholder relationships and project performance."
  },
  {
    id: 8,
    title: "Alfords Point Road Widening",
    subtitle: "Road infrastructure upgrade",
    sector: "Roads & Civil",
    category: "roads",
    value: "$30M",
    location: "Sydney, NSW",
    description: "Road widening and upgrade project requiring strategic coordination and delivery oversight.",
    role: "Project Director - Delivery oversight",
    scope: [
      "Road design validation and construction oversight",
      "Community and stakeholder engagement",
      "Traffic management and safety coordination",
      "Environmental and heritage compliance"
    ],
    outcomes: "Completed road upgrade maintaining community access and safety. Strong stakeholder engagement and minimal disruption."
  },
  {
    id: 9,
    title: "Subdivision Development - Austral",
    subtitle: "232-237 Seventh Avenue",
    sector: "Commercial Development",
    category: "commercial",
    value: "$25M",
    location: "Austral, NSW",
    description: "Large-scale subdivision development requiring strategic project direction.",
    role: "Project Director - Development oversight",
    scope: [
      "Subdivision planning and approval coordination",
      "Contractor management and delivery oversight",
      "Stakeholder and authority coordination",
      "Quality and compliance assurance"
    ],
    outcomes: "Successfully delivered subdivision meeting planning and quality requirements. Strong coordination across authorities and contractors."
  },
  {
    id: 10,
    title: "Commercial Development - Vineyard",
    subtitle: "339 Commercial Road",
    sector: "Commercial Development",
    category: "commercial",
    value: "$20M",
    location: "Vineyard, NSW",
    description: "Commercial development project requiring expert project direction and oversight.",
    role: "Project Director - Development delivery",
    scope: [
      "Commercial development planning and coordination",
      "Design and construction oversight",
      "Contractor and consultant management",
      "Schedule and cost control"
    ],
    outcomes: "Delivered commercial development on time and budget. Maintained quality standards and stakeholder satisfaction."
  },
  {
    id: 11,
    title: "Surteco Warehouse",
    subtitle: "Design and construct facility",
    sector: "Industrial",
    category: "commercial",
    value: "$10M+",
    location: "Australia",
    description: "Industrial warehouse design and construct project.",
    role: "Project Oversight - Client-side representation",
    scope: [
      "Design development and validation",
      "Construction oversight and quality control",
      "Contractor coordination and performance",
      "Schedule and budget management"
    ],
    outcomes: "Delivered functional warehouse facility meeting operational requirements. Strong cost and schedule performance."
  },
  {
    id: 12,
    title: "Medical Centre Construction",
    subtitle: "Healthcare facility",
    sector: "Commercial",
    category: "commercial",
    value: "$10M",
    location: "Penrith, NSW",
    description: "Medical centre construction requiring specialized project oversight.",
    role: "Project Director - Healthcare construction oversight",
    scope: [
      "Healthcare compliance and regulatory coordination",
      "Construction quality and safety management",
      "Stakeholder and authority engagement",
      "Schedule and cost control"
    ],
    outcomes: "Completed medical facility meeting healthcare standards and regulatory requirements. Strong quality and compliance performance."
  },
  {
    id: 13,
    title: "Amazon Flex Facility",
    subtitle: "Construction and fit-out",
    sector: "Commercial & Industrial",
    category: "commercial",
    value: "$5M",
    location: "Newcastle, NSW",
    description: "Commercial facility construction and specialized fit-out for logistics operations.",
    role: "Project Oversight - Delivery coordination",
    scope: [
      "Construction and fit-out coordination",
      "Quality control and compliance",
      "Contractor performance management",
      "Schedule delivery and handover"
    ],
    outcomes: "Delivered facility meeting operational and technical requirements. Successful handover and commissioning."
  }
];

export const processSteps = [
  {
    id: 1,
    title: "Discover",
    description: "Requirements & Constraints",
    details: "Understanding project objectives, constraints, stakeholder expectations, and establishing clear success criteria."
  },
  {
    id: 2,
    title: "Plan",
    description: "Strategy & Governance",
    details: "Developing comprehensive project strategies, governance frameworks, and delivery plans aligned with organizational objectives."
  },
  {
    id: 3,
    title: "Deliver",
    description: "Oversight & Coordination",
    details: "Active project oversight, contractor coordination, and stakeholder management ensuring delivery aligns with plans."
  },
  {
    id: 4,
    title: "Assure",
    description: "Risk, Reporting, Quality",
    details: "Continuous risk management, performance reporting, and quality assurance throughout project lifecycle."
  },
  {
    id: 5,
    title: "Close",
    description: "Handover & Lessons Learned",
    details: "Structured project closeout, successful handover, and capture of lessons learned for continuous improvement."
  }
];

export const navigationLinks = [
  { id: 1, name: "Home", href: "/" },
  { id: 2, name: "About", href: "/about" },
  { id: 3, name: "Services", href: "/services" },
  { id: 4, name: "Portfolio", href: "/portfolio" },
  { id: 5, name: "Contact", href: "/contact" }
];
